import { Tooltip } from '@mui/material';
import { ReactNode, useId, useState } from 'react';

// Hauser's data-viz system, ported from Tradehaus's (tx-vz-*, docs/design-language/source/styles/tx.css ~972 and
// components/tx/PipelineViz.jsx, the gold standard). One version of each mark so every chart in the app reads the
// same: donut slices and ring arcs with 2px corners and 2px gaps, thin rounded bars on a baseline with the current one
// in lime, a 1.5px line with its latest point marked, dots for counts. Data hues go on the marks, never on panel fills.
//
// Use these instead of drawing a chart by hand. Recharts stays for real axes and time series (styled per lineSeries
// below); these are the glanceable visuals that sit in tiles, rows and panels.

const ink = (a: number) => `rgba(var(--tx-ink-rgb), ${a})`;
const LIME = '#C0FF02';
const font = "'Manrope', sans-serif";

export const SLICE_R = 2;
export const SLICE_GAP = 2;

// a ring segment with SLICE_R corners and a gap at each end: inset by the corner radius, then stroked at twice it with
// round joins, which rounds each corner by exactly that much
export function slicePath(cx: number, cy: number, r: number, w: number, gap: number, a0: number, a1: number, cr = SLICE_R) {
  const ro = r + w / 2 - cr;
  const ri = r - w / 2 + cr;
  const d = gap / 2 + cr;
  const o0 = a0 + d / ro;
  const o1 = a1 - d / ro;
  const i0 = a0 + d / ri;
  const i1 = a1 - d / ri;
  if (i1 <= i0) return null;
  const pt = (rad: number, a: number) => `${(cx + rad * Math.sin(a)).toFixed(2)} ${(cy - rad * Math.cos(a)).toFixed(2)}`;
  const big = o1 - o0 > Math.PI ? 1 : 0;
  return `M${pt(ro, o0)}A${ro} ${ro} 0 ${big} 1 ${pt(ro, o1)}L${pt(ri, i1)}A${ri} ${ri} 0 ${big} 0 ${pt(ri, i0)}Z`;
}

export interface VizPart {
  key: string;
  label: string;
  value: number;
  color: string;
}

// Donut: a mix as slices (Pipeline's mix donut). A light figure in the middle, its caption beneath. Hovering a slice
// names it and dims the rest. ringRatio is the ring's width against the size (Tradehaus: 20 on 224)
export const Donut = ({
  parts,
  size = 180,
  label,
  sub,
  ringRatio = 0.09,
  format = (v) => String(v),
}: {
  parts: VizPart[];
  size?: number;
  label?: ReactNode;
  sub?: ReactNode;
  ringRatio?: number;
  format?: (v: number) => string;
}) => {
  const [hover, setHover] = useState<string | null>(null);
  const sw = Math.max(8, Math.round(size * ringRatio));
  const r = size / 2 - sw / 2 - 2;
  const cx = size / 2;
  const total = parts.reduce((a, p) => a + Math.max(0, p.value), 0);
  const shares = parts.map((p) => (total ? Math.max(0, p.value) / total : 0));
  const starts = shares.map((_, i) => shares.slice(0, i).reduce((a, s) => a + s, 0));
  const solo = shares.filter((s) => s > 0).length === 1;
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: size, aspectRatio: '1', fontFamily: font }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', width: '100%', height: '100%' }} role="img">
        {!total && <circle cx={cx} cy={cx} r={r} fill="none" stroke={ink(0.1)} strokeWidth={sw} />}
        {parts.map((p, i) => {
          if (!shares[i]) return null;
          const dim = hover && hover !== p.key ? 0.4 : 1;
          const tip = `${p.label} · ${format(p.value)}`;
          const common = {
            opacity: dim,
            onMouseEnter: () => setHover(p.key),
            onMouseLeave: () => setHover(null),
            style: { transition: 'opacity 0.12s ease', cursor: 'default' },
          };
          if (solo) {
            return (
              <Tooltip key={p.key} title={tip} placement="top">
                <circle cx={cx} cy={cx} r={r} fill="none" stroke={p.color} strokeWidth={sw} {...common} />
              </Tooltip>
            );
          }
          const d = slicePath(cx, cx, r, sw, SLICE_GAP, starts[i] * 2 * Math.PI, (starts[i] + shares[i]) * 2 * Math.PI);
          return d ? (
            <Tooltip key={p.key} title={tip} placement="top">
              <path d={d} fill={p.color} stroke={p.color} strokeWidth={SLICE_R * 2} strokeLinejoin="round" {...common} />
            </Tooltip>
          ) : null;
        })}
      </svg>
      {(label != null || sub != null) && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}>
          {label != null && (
            <b
              style={{
                fontSize: Math.round(size * 0.15),
                fontWeight: 300,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                color: 'var(--tx-text)',
                fontVariantNumeric: 'tabular-nums',
              }}>
              {label}
            </b>
          )}
          {sub != null && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)', marginTop: 4 }}>{sub}</span>}
        </div>
      )}
    </div>
  );
};

// Ring: one share as an arc on a quiet track (a progress ring), cornered like the donut's slices. A number in a ring
// takes no % sign
export const Ring = ({
  value,
  size = 72,
  color = LIME,
  width = 9,
  children,
}: {
  /** 0–1 */
  value: number | null;
  size?: number;
  color?: string;
  width?: number;
  children?: ReactNode;
}) => {
  const id = useId();
  const r = 42;
  const v = value == null ? 0 : Math.min(1, Math.max(0, value));
  const minA = (2 * SLICE_R + 1) / (r - width / 2 + SLICE_R);
  const d = v > 0 && v < 0.999 ? slicePath(50, 50, r, width, 0, 0, Math.max(v * 2 * Math.PI, minA)) : null;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg viewBox="0 0 100 100" aria-hidden style={{ display: 'block', width: '100%', height: '100%' }}>
        <circle cx="50" cy="50" r={r} fill="none" stroke={ink(0.14)} strokeWidth={width} />
        {v > 0 &&
          (d ? (
            <path d={d} fill={color} stroke={color} strokeWidth={SLICE_R * 2} strokeLinejoin="round" />
          ) : (
            <circle key={id} cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth={width} />
          ))}
      </svg>
      {children != null && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: font,
            fontSize: Math.round(size * 0.22),
            fontWeight: 300,
            letterSpacing: '-0.03em',
            color: 'var(--tx-text)',
            fontVariantNumeric: 'tabular-nums',
          }}>
          {children}
        </div>
      )}
    </div>
  );
};

// Bars: thin rounded bars on a baseline (Pipeline's weekly bars). Neutral ink by default; the current bar (last, or
// `now`) in lime; a bar may carry its own data colour. Labels optional, in meta under each bar
export const Bars = ({
  data,
  height = 28,
  now,
  gap = 3,
  labels,
  format = (v) => String(v),
}: {
  data: { label: string; value: number; color?: string }[];
  height?: number;
  /** index of the current bar to light in lime (-1 for none); defaults to none */
  now?: number;
  gap?: number;
  labels?: boolean;
  format?: (v: number) => string;
}) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div style={{ fontFamily: font }}>
      <div role="img" style={{ display: 'flex', alignItems: 'flex-end', gap, height }}>
        {data.map((d, i) => (
          <Tooltip key={`${d.label}-${i}`} title={`${d.label} · ${format(d.value)}`} placement="top">
            <i
              style={{
                flex: 1,
                height: `${Math.max(d.value ? 12 : 4, (d.value / max) * 100)}%`,
                borderRadius: 2,
                background: i === now ? LIME : d.color ?? ink(0.28),
                cursor: 'default',
              }}
            />
          </Tooltip>
        ))}
      </div>
      {labels && (
        <div style={{ display: 'flex', gap, marginTop: 6 }}>
          {data.map((d, i) => (
            <span
              key={`${d.label}-${i}`}
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: 10,
                fontWeight: 500,
                color: 'var(--tx-faint)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {d.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Spark: a thin line with the latest point marked in lime, no fill
export const Spark = ({ data, height = 28, color = 'var(--tx-text)' }: { data: number[]; height?: number; color?: string }) => {
  const W = 120;
  const n = data.length;
  if (n < 2) return <div style={{ height }} />;
  const max = Math.max(1, ...data);
  const x = (i: number) => (i * W) / (n - 1);
  const y = (v: number) => height - 3 - (v / max) * (height - 8);
  return (
    <div style={{ position: 'relative', width: '100%', height }} aria-hidden>
      <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '100%', overflow: 'visible' }}>
        <polyline
          points={data.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')}
          fill="none"
          stroke={color}
          strokeOpacity={0.8}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          left: '100%',
          top: `${(y(data[n - 1]) / height) * 100}%`,
          width: 7,
          height: 7,
          margin: '-3.5px 0 0 -3.5px',
          borderRadius: '50%',
          background: LIME,
        }}
      />
    </div>
  );
};

// Dots: one per item, filled when it counts, faint otherwise (Pipeline's dots; Advisor-Client's client dots)
export const Dots = ({ items, size = 8 }: { items: { key: string; on: boolean; color?: string; label?: string }[]; size?: number }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minHeight: 28, alignContent: 'center' }}>
    {items.map((it) => (
      <i
        key={it.key}
        title={it.label}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: it.on ? it.color ?? 'var(--tx-text)' : ink(0.22),
        }}
      />
    ))}
  </div>
);

// Legend: the mix's key beside a donut. A 2px tick in the part's colour, its name in meta, its value in ink
export const Legend = ({ parts, format = (v) => String(v) }: { parts: VizPart[]; format?: (v: number) => string }) => (
  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, fontFamily: font }}>
    {parts.map((p) => (
      <li key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', minWidth: 0 }}>
        <span style={{ width: 2, height: 12, borderRadius: 1, background: p.color, flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</span>
        <span style={{ color: 'var(--tx-text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{format(p.value)}</span>
      </li>
    ))}
  </ul>
);

// For Recharts time series: the house line and axis styling (thin lines, no fill or a faint one, quiet axes, no grid
// clutter). Spread into <Line {...lineSeries(color)} /> and <XAxis {...quietAxis} />
export const lineSeries = (color: string) => ({
  stroke: color,
  strokeWidth: 1.5,
  dot: false,
  activeDot: { r: 3.5, fill: LIME, stroke: 'none' },
  isAnimationActive: false,
});
export const quietAxis = {
  axisLine: false,
  tickLine: false,
  tick: { fill: 'var(--tx-faint)', fontSize: 10, fontFamily: font },
};
export const quietGrid = { stroke: ink(0.06), vertical: false };
